import { Route, Switch, useParams } from 'react-router-dom';
import {
  SubNavigationNavBar,
  SubNavigationNavLink,
} from '__components/Navigation/SubNavigation';
import { subroutes } from './navigation';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import { ControlBar } from './ControlBar';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import SmartConnectorService from 'services/SmartConnectorService';
import CustomObjectsService from 'services/CustomObjectsService';
import {
  CustomObjectLike,
  SmartConnector,
  SmartConnectorMetaData,
} from '__pages/SmartConnectors/types';
import { setError } from 'store/errors';
import { useCallback, useState } from 'react';
import { useSmartConnectorAccess } from '__pages/SmartConnectors/hooks/useSmartConnectorAccess';
import Loader from '__components/Kizen/Loader';
import { StyledPage } from './styles';
import { useSmartConnectorTitle } from '../hooks/useSmartConnectorTitle';

export const SmartConnectorSettingsPage = () => {
  const params = useParams<{ id: string; subpage: string }>();
  const [smartConnector, setSmartConnector] = useState<SmartConnector>();
  useSmartConnectorTitle(smartConnector);
  const { id } = params;

  const { smartConnectorAccess } = useSmartConnectorAccess(
    smartConnector?.owner?.id
  );

  const dispatch = useDispatch();

  const [runKey, setRunKey] = useState(0);

  const onStartRun = useCallback(() => setRunKey((prev) => prev + 1), []);

  const { value: { customObject = null, metaData = null } = {}, loading } =
    useAsync(async () => {
      try {
        const [smartConnectorResponse, metadataResponse] = await Promise.all([
          SmartConnectorService.getSmartConnector(id),
          SmartConnectorService.getSmartConnectorMetaData(),
        ]);
        setSmartConnector(smartConnectorResponse.data);
        const customObject = await CustomObjectsService.getCustomObjectDetails(
          smartConnectorResponse.data.custom_object
        );

        return { customObject, metaData: metadataResponse.data } as {
          customObject: CustomObjectLike;
          metaData: SmartConnectorMetaData;
        };
      } catch (err: any) {
        if (err.isAxiosError) {
          dispatch(setError({ code: err.response?.status }));
        }
      }
    }, [id]);

  return (
    <StyledPage>
      <Loader loading={loading}>
        {smartConnector && customObject && metaData ? (
          <>
            <ControlBar
              smartConnector={smartConnector}
              access={smartConnectorAccess}
              onStartRun={onStartRun}
            />
            <SubNavigationNavBar>
              {Object.values(subroutes).map((route, i, arr) => {
                return (
                  <SubNavigationNavLink
                    key={route.path}
                    route={route}
                    isLast={i === arr.length - 1}
                    numberOfLinks={arr.length}
                    params={params}
                  />
                );
              })}
            </SubNavigationNavBar>

            <PageSizing>
              <ContentWidth>
                <Switch>
                  {Object.values(subroutes).map(
                    ({ component: Component, path, exact }) =>
                      Component ? (
                        <Route
                          key={path}
                          path={path}
                          exact={exact !== false}
                          render={() => (
                            <Component
                              smartConnector={smartConnector}
                              customObject={customObject}
                              setSmartConnector={setSmartConnector}
                              access={smartConnectorAccess}
                              metaData={metaData}
                              runKey={runKey}
                            />
                          )}
                        />
                      ) : null
                  )}
                </Switch>
              </ContentWidth>
            </PageSizing>
          </>
        ) : null}
      </Loader>
    </StyledPage>
  );
};
